import { useToast } from '@chakra-ui/toast';
import firebase from 'firebase';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FirebaseUser, UserMatchType } from '../types/types';
import { isInDevelopment } from '../utils/detectEnv';
import { expired } from '../utils/expired';

export const AuthContext = createContext<firebase.User | null>(null);

export const AuthProvider: React.FC = ({ children }) => {
  const auth = firebase.auth();
  const [currentUser, setCurrentUser] = useState<firebase.User | null>(
    auth.currentUser
  );
  const history = useHistory();
  const toast = useToast();

  useEffect(() => {
    const unlisten = auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        setCurrentUser(authUser);
        firebase
          .app()
          .functions(isInDevelopment ? undefined : 'europe-west3')
          .httpsCallable('checkUserStatus')({ email: authUser.email })
          .then((result) => {
            const checkUserStatusData =
              result.data as NonNullable<UserMatchType>;
            const docRef = firebase
              .firestore()
              .collection('users')
              .doc(authUser.uid);

            docRef
              .get()
              .then(async (snapshot) => {
                const firestoreUser =
                  snapshot.exists && (snapshot.data() as FirebaseUser);

                if (firestoreUser && !firestoreUser.isAdmin) {
                  if (
                    checkUserStatusData.error === undefined &&
                    (firestoreUser.status === 'active' ||
                      firestoreUser.status === 'inactive')
                  ) {
                    if (
                      !checkUserStatusData.found &&
                      firestoreUser.status === 'active'
                    ) {
                      docRef.update({ status: 'inactive' });
                    }
                    if (
                      checkUserStatusData.found &&
                      firestoreUser.status === 'inactive'
                    ) {
                      docRef.update({ status: 'active' });
                    }
                  } else if (
                    firestoreUser.status === 'trial' &&
                    expired(firestoreUser)
                  ) {
                    if (checkUserStatusData.found) {
                      docRef.update({ status: 'active' });
                    }
                  }
                  if (
                    checkUserStatusData.error &&
                    checkUserStatusData.error !== ''
                  ) {
                    console.log(checkUserStatusData.error);
                    toast({
                      title: 'Fehler beim Datenabgleich',
                      description: checkUserStatusData.error,
                      status: 'error',
                    });
                  }
                }
              })
              .catch((e) => {
                console.log(e);
                toast({
                  title: 'Fehler beim Datenabgleich',
                  description: e.toString(),
                  status: 'error',
                });
              });
          });
      } else {
        setCurrentUser(null);
        !(
          window.location.pathname.startsWith('/login') ||
          window.location.pathname.startsWith('/iframe') ||
          window.location.pathname.startsWith('/landing')
        ) &&
          history &&
          history.push('/login');
      }
    });

    return () => {
      unlisten();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={currentUser}>{children}</AuthContext.Provider>
  );
};

export const useCurrentUser = () => useContext(AuthContext);
